import { useCallback } from "react";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import Icon from "~/components/shared/Icon";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  HoverCardPortal,
} from "~/components/shared/HoverCard";
import SharedBadge from "~/components/shared/Badge";
import { Badge } from "~/components/build/Show/pages/steps/list/components/Badge";
import { AgentIcon, MACHINE_TYPES } from "~/components/job/Job/Agent";
import { CommandJob, OS } from "~/components/build/Show/lib/types/CommandJob";

function getQueueIcon(hosted: boolean): string {
  if (hosted) {
    return "heroicons/16/solid/cloud";
  }
  return "heroicons/16/solid/server-stack";
}

function QueueIcon({ hosted, os }: { hosted: boolean; os?: OS }) {
  if (hosted && os && os !== "unknown") {
    return <AgentIcon os={os} />;
  }
  return <Icon icon={getQueueIcon(hosted)} className="h-3 w-3 shrink-0" />;
}

function QueueHoverContent({ job, queuePath }: { job: CommandJob; queuePath?: string }) {
  const queueName = job.clusterQueue?.key;
  const { instanceShape, hostedOSVersion, hosted } = job;

  return (
    <div className="grid grid-cols-[1fr_2fr] gap-x-2.5 gap-y-1.5">
      <span className="font-semibold">Agent</span>
      <a href={job.agentPath} className="max-w-full truncate text-inherit hover:text-inherit">
        {job.agentName}
      </a>

      {queueName && (
        <>
          <span className="font-semibold">Queue</span>
          <a
            href={queuePath ?? job.agentsPath}
            className="max-w-full truncate text-inherit hover:text-inherit"
          >
            {queueName}
          </a>
        </>
      )}

      {hosted && instanceShape && instanceShape.operatingSystem !== "unknown" && (
        <>
          <span className="font-semibold">OS</span>
          <div>
            <SharedBadge className="charcoal-300 bg-gray-100">
              <img
                src={MACHINE_TYPES[instanceShape.operatingSystem].logo}
                width="14"
                height="14"
                style={{ verticalAlign: "text-top" }}
                alt={MACHINE_TYPES[instanceShape.operatingSystem].label}
              />
              {MACHINE_TYPES[instanceShape.operatingSystem].label} {hostedOSVersion}
            </SharedBadge>
          </div>
          <span className="font-semibold">Architecture</span>
          <div>
            <SharedBadge className="charcoal-300 bg-gray-100">
              {instanceShape.architecture}
            </SharedBadge>
          </div>
          <span className="font-semibold">System</span>
          <div>
            <SharedBadge className="charcoal-300 bg-gray-100">
              {instanceShape.cpu} vCPU · {instanceShape.memory} GB RAM
            </SharedBadge>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Resolves the job for the hover card content.
 * Accepts a direct `job` prop (from JobLabel) or a `latestJobId` (from StepItem)
 * which is looked up from the Zustand store only when the hover card is open.
 */
function useResolvedJob(job?: CommandJob, latestJobId?: string): CommandJob | undefined {
  const storeJob = useBuildStore((state) =>
    latestJobId && !job ? (state.jobs.get(latestJobId) as CommandJob | undefined) : undefined,
  );
  return job ?? storeJob;
}

/**
 * Lazy-mounted hover content that only subscribes to the store when rendered.
 */
function LazyQueueHoverContent({
  job,
  latestJobId,
  queuePath,
}: {
  job?: CommandJob;
  latestJobId?: string;
  queuePath?: string;
}) {
  const resolvedJob = useResolvedJob(job, latestJobId);

  if (!resolvedJob) {
    return null;
  }

  return <QueueHoverContent job={resolvedJob} queuePath={queuePath} />;
}

interface QueueLabelProps {
  queueKey: string;
  os?: OS;
  job?: CommandJob;
  latestJobId?: string;
}

export function QueueLabel({ queueKey, os, job, latestJobId }: QueueLabelProps) {
  const { build } = useBuild();
  const clusterQueue = build.clusterQueues.find((queue) => queue.key === queueKey);
  const hosted = clusterQueue?.hosted ?? false;
  const hasHoverData = Boolean(job || latestJobId);

  const handleClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const badge = (
    <Badge className="text-charcoal-500 inline-flex items-center gap-1 group-hover/queue:border-gray-600">
      <QueueIcon hosted={hosted} os={os} />
      {queueKey}
    </Badge>
  );

  const trigger = clusterQueue?.path ? (
    <a
      href={clusterQueue.path}
      onClick={handleClick}
      className="group/queue focus:outline-hidden rounded-full text-inherit hover:text-inherit focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
    >
      {badge}
    </a>
  ) : (
    <span className="group/queue">{badge}</span>
  );

  if (!hasHoverData) {
    return trigger;
  }

  return (
    <HoverCard>
      <HoverCardTrigger asChild={true}>{trigger}</HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent className="z-30" size="max-lg" onClick={handleClick}>
          <LazyQueueHoverContent
            job={job}
            latestJobId={latestJobId}
            queuePath={clusterQueue?.path}
          />
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
}
